<template>
  <div class="container__date-wrapper">
    <div class="configuration-tabs configuration-tabs--left container--date">
      <div class="configuration-tabs--inner">
        <DatePicker
          v-model="periodValue"
          :disabled-date="(d) =>  d > new Date() || d < firstDate"
          :default-value="periodValue"
          :shortcuts="getDateShortcuts()"
          title-format="D MMMM YYYY"
          format=" "
          :lang="{ formatLocale: { firstDayOfWeek: 1 } }"
          :clearable="false"
          :editable="false"
          range-separator="Select period"
          range
        >
        </DatePicker>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import 'vue2-datepicker/index.css'
import DatePicker from 'vue2-datepicker'

@Component({
  components: { DatePicker }
})
export default class Controls extends Vue {
  @Prop({ required: true }) readonly currentPeriod!: Date[]
  @Prop({ default: () => ({}) }) readonly lastValues!: { month: number, year: number }

  periodValue: Date[] = []

  mounted() {
    this.periodValue = this.currentPeriod;
  }

  getDateShortcuts() {
    const today = new Date();

    const isInRange = (start: Date, end: Date) =>
      !(end < this.firstDate || start > today);

    const clampRange = (start: Date, end: Date): [Date, Date] => [
      new Date(Math.max(start.getTime(), this.firstDate.getTime())),
      new Date(Math.min(end.getTime(), today.getTime())),
    ];

    const shortcuts = [{
      text: 'Today',
      onClick: () => this.periodValue = [today, today]
    }];

    // Yesterday
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (isInRange(yesterday, yesterday)) {
      shortcuts.push({
        text: 'Yesterday',
        onClick: () => this.periodValue = [yesterday, yesterday]
      });
    }

    // Last 7 days
    const last7Start = new Date(today);
    last7Start.setDate(today.getDate() - 6);
    if (isInRange(last7Start, today)) {
      const [start, end] = clampRange(last7Start, today);
      shortcuts.push({
        text: 'Last 7 days',
        onClick: () => this.periodValue = [start, end]
      });
    }

    // Last 30 days
    const last30Start = new Date(today);
    last30Start.setDate(today.getDate() - 29);
    if (isInRange(last30Start, today)) {
      const [start, end] = clampRange(last30Start, today);
      shortcuts.push({
        text: 'Last 30 days',
        onClick: () => this.periodValue = [start, end]
      });
    }

    // This week (Mon–today)
    const startOfWeek = new Date(today);
    const day = startOfWeek.getDay();
    const diffToMonday = day === 0 ? 6 : day - 1;
    startOfWeek.setDate(startOfWeek.getDate() - diffToMonday);
    if (isInRange(startOfWeek, today)) {
      const [start, end] = clampRange(startOfWeek, today);
      shortcuts.push({
        text: 'This week',
        onClick: () => this.periodValue = [start, end]
      });
    }

    // Last week (Mon–Sun)
    const lastWeekStart = new Date(startOfWeek);
    lastWeekStart.setDate(lastWeekStart.getDate() - 7);
    const lastWeekEnd = new Date(startOfWeek);
    lastWeekEnd.setDate(lastWeekEnd.getDate() - 1);
    if (isInRange(lastWeekStart, lastWeekEnd)) {
      const [start, end] = clampRange(lastWeekStart, lastWeekEnd);
      shortcuts.push({
        text: 'Last week',
        onClick: () => this.periodValue = [start, end]
      });
    }

    // This month
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    if (isInRange(startOfMonth, today)) {
      const [start, end] = clampRange(startOfMonth, today);
      shortcuts.push({
        text: 'This month',
        onClick: () => this.periodValue = [start, end]
      });
    }

    // Last month
    const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
    if (isInRange(lastMonthStart, lastMonthEnd)) {
      const [start, end] = clampRange(lastMonthStart, lastMonthEnd);
      shortcuts.push({
        text: 'Last month',
        onClick: () => this.periodValue = [start, end]
      });
    }

    // This year
    const startOfYear = new Date(today.getFullYear(), 0, 1);
    if (isInRange(startOfYear, today)) {
      const [start, end] = clampRange(startOfYear, today);
      shortcuts.push({
        text: 'This year',
        onClick: () => this.periodValue = [start, end]
      });
    }

    // Last year
    const lastYearStart = new Date(today.getFullYear() - 1, 0, 1);
    const lastYearEnd = new Date(today.getFullYear() - 1, 11, 31);
    if (isInRange(lastYearStart, lastYearEnd)) {
      const [start, end] = clampRange(lastYearStart, lastYearEnd);
      shortcuts.push({
        text: 'Last year',
        onClick: () => this.periodValue = [start, end]
      });
    }

    // From start
    shortcuts.push({
      text: 'From start',
      onClick: () => this.periodValue = [this.firstDate, today]
    });

    return shortcuts;
  }

  @Watch('periodValue')
  onPeriodChange() {
    this.$emit('setCurrentPeriod', this.periodValue)
  }

  get firstDate() {
    if (!this.lastValues) {
      return new Date();
    }
    return new Date(this.lastValues.year, this.lastValues.month, 1)
  }
}
</script>


<style lang="scss">
@import "../../assets/scss/variables";

.container__date-wrapper {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  position: relative;
}

.mx-table {
  th, td {
    text-align: center !important;
  }
}

.mx-input {
  height: 36px;
  width: 140px;
  background: rgba(0, 0, 0, 0);
  border: rgba(0, 0, 0, 0);
  color: white;
  cursor: pointer;
}

.mx-datepicker-range {
  width: auto;
}

.mx-icon-calendar {
  color: white;
  cursor: pointer;
}

.mx-input-wrapper {
  justify-self: center;
}

.mx-datepicker-sidebar {
  width: auto;

  > *:nth-child(2n):not(:last-child) {
    border-bottom: 1px solid rgb(216, 216, 216);
    border-radius: 0;
  }
}
</style>
